import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Star, 
  Eye, 
  CheckCircle,
  FileText,
  TrendingUp,
  Download
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { usePermissions } from '@hooks/usePermissions';
import { ADMIN_PERMISSIONS, FEEDBACK_TYPES, ADMIN_ROUTES } from '@constants/admin.constants';
import feedbackAdminService from '@services/admin/feedback.admin.service';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import StatsCard from '@components/admin/StatsCard';
import FilterBar from '@components/admin/FilterBar';
import Pagination from '@components/admin/Pagination';
import PermissionGuard from '@components/admin/PermissionGuard';
import toast from 'react-hot-toast';

const FeedbackManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  const [stats, setStats] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    rating: 'all',
    reviewed: 'all',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState(null);

  const filterOptions = [
    { value: 'all', label: 'All Feedback' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'high_rating', label: 'High Rating (4-5)' },
    { value: 'low_rating', label: 'Low Rating (1-2)' },
  ];

  useEffect(() => {
    if (!hasPermission(ADMIN_PERMISSIONS.VIEW_FEEDBACK)) {
      toast.error('Access denied');
      navigate('/admin/dashboard');
      return;
    }
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, feedbackRes] = await Promise.all([
        feedbackAdminService.getStats(),
        feedbackAdminService.getAllFeedback(filters)
      ]);

      if (statsRes.success) {
        setStats(statsRes.data);
      }

      if (feedbackRes.success) {
        setFeedbacks(Array.isArray(feedbackRes.data?.data?.feedbacks) ? feedbackRes?.data?.data.feedbacks : []);
        setPagination(feedbackRes.data?.data?.pagination || {});
      }
    } catch (error) {
      toast.error('Failed to load feedback data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleViewDetails = (feedbackId) => {
    navigate(`${ADMIN_ROUTES.FEEDBACK}/${feedbackId}`);
  };

  const handleMarkReviewed = async (feedbackId) => {
    try {
      const response = await feedbackAdminService.updateReview(feedbackId, {
        isReviewed: true,
        adminNotes: 'Reviewed'
      });

      if (response.success) {
        toast.success('Feedback marked as reviewed');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update feedback');
    }
  };

  const getFeedbackTypeLabel = (type) => {
    const labels = {
      general: 'General',
      feature_request: 'Feature Request',
      bug_report: 'Bug Report',
      ui_ux: 'UI/UX',
      performance: 'Performance',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const getFeedbackTypeBadge = (type) => {
    const badges = {
      general: 'bg-blue-100 text-blue-700',
      feature_request: 'bg-purple-100 text-purple-700',
      bug_report: 'bg-red-100 text-red-700',
      ui_ux: 'bg-green-100 text-green-700',
      performance: 'bg-yellow-100 text-yellow-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return badges[type] || badges.other;
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <PermissionGuard requiredPermission={ADMIN_PERMISSIONS.VIEW_FEEDBACK}>
      <div className="min-h-full bg-gray-50 p-3">
          {/* <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary-900 mb-2">
              Feedback Management
            </h1>
            <p className="text-primary-600">
              Review and manage user feedback
            </p>
          </div> */}

          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <StatsCard
                title="Total Feedback"
                value={stats.totalFeedback || 0}
                icon={MessageSquare}
                color="primary"
              />
              <StatsCard
                title="Pending Review"
                value={stats.pendingReview || 0}
                icon={FileText}
                color="yellow"
              />
              <StatsCard
                title="Average Rating"
                value={stats.averageRating ? stats.averageRating.toFixed(1) : '0.0'}
                icon={Star}
                color="green"
              />
              <StatsCard
                title="This Month"
                value={stats.thisMonth || 0}
                icon={TrendingUp}
                color="blue"
              />
            </div>
          )}

          <Card className="mb-3 p-4">
            <FilterBar
              searchValue={filters.search}
              onSearchChange={(value) => handleFilterChange('search', value)}
              filters={filterOptions}
              activeFilter={filters.type}
              onFilterChange={(value) => handleFilterChange('type', value)}
              placeholder="Search by user email or feedback content..."
            >
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => feedbackAdminService.exportFeedback(filters)}
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button> */}
            </FilterBar>
          </Card>

          <Card className="overflow-hidden">
            <div className="p-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-primary-900">
                User Feedback
              </h2>
            </div>

            {loading ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner size="lg" />
              </div>
            ) : feedbacks.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-16 h-16 text-primary-300 mx-auto mb-4" />
                <p className="text-primary-600">No feedback found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rating
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Feedback
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {feedbacks.map((feedback) => (
                        <tr key={feedback.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {feedback.user?.email || 'Unknown'}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {feedback.userId?.slice(0, 8)}...
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {renderStars(feedback.rating)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getFeedbackTypeBadge(feedback.feedbackType)}`}>
                              {getFeedbackTypeLabel(feedback.feedbackType)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-sm text-gray-900 max-w-md truncate">
                              {feedback.feedbackMessage || 'No message'}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {new Date(feedback.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {feedback.isReviewed ? (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                Reviewed
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDetails(feedback.id)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              {!feedback.isReviewed && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleMarkReviewed(feedback.id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Mark Reviewed
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pagination && (
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.total}
                    itemsPerPage={pagination.limit}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            )}
          </Card>
      </div>
    </PermissionGuard>
  );
};

export default FeedbackManagement;
